import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import Modal from './Modal';
import ConfirmationModal from './ConfirmationModal';
import TenantProductsField, { APP_SUBDOMAIN_TAKEN_MESSAGE } from './TenantProductsField';
import { apiClient, ApiError, TenantResponse, TenantUpdateRequest, TenantLog, SdlOnboardingChecklist, CreateTenantChecklist, BuildClientContentChecklist } from '../lib/api';
import { defaultExpertAppSubdomain } from '../lib/utils';
//import AggressivenessSlider from './common/AggressivenessSlider'; // Import the slider

interface EditTenantModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenant: TenantResponse | null;
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

const EditTenantModal: React.FC<EditTenantModalProps> = ({
  isOpen,
  onClose,
  tenant,
  onSuccess,
  onError,
}) => {
  const [name, setName] = useState('');
  const [status, setStatus] = useState('active');
  const [tenantType, setTenantType] = useState('product');
  const [goal, setGoal] = useState('');
  const [aggressiveness, setAggressiveness] = useState(5.0);
  const [error, setError] = useState('');
  const [tenantLogs, setTenantLogs] = useState<TenantLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [showCacheClearModal, setShowCacheClearModal] = useState(false);
  const [cacheMessage, setCacheMessage] = useState('');
  const [cacheMessageType, setCacheMessageType] = useState<'success' | 'error' | ''>('');
  const [createTenantChecklistExpanded, setCreateTenantChecklistExpanded] = useState(false);
  const [sdlChecklistExpanded, setSdlChecklistExpanded] = useState(false);
  const [buildContentChecklistExpanded, setBuildContentChecklistExpanded] = useState(false);
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);
  const [clientAdminEnabled, setClientAdminEnabled] = useState(true);
  const [domainExpertEnabled, setDomainExpertEnabled] = useState(false);
  const [appSubdomain, setAppSubdomain] = useState('');
  const [appSubdomainError, setAppSubdomainError] = useState<string | null>(null);
  const [pendingProductOff, setPendingProductOff] = useState<'client_admin' | 'domain_expert' | null>(null);

  const defaultCreateTenantChecklist: CreateTenantChecklist = {
    tenant_created_on_lawmabase: false,
    dns_entries_added: false,
    custom_domains_added_in_azure: false,
    ssl_certificates_created_and_tested: false,
  };

  const defaultChecklist: SdlOnboardingChecklist = {
    crawl_mds_uploaded: false,
    marketing_audit_uploaded: false,
    competitor_crawl_uploaded: false,
    questionaire_uploaded: false,
    other_mds_uploaded: false,
    sdl_generated: false,
    sdl_uploaded_to_qm: false,
    complete_sdl_qa_pass: false,
  };

  const defaultBuildContentChecklist: BuildClientContentChecklist = {
    assets_added: false,
    faq_category_labels_created: false,
    ctas_setup: false,
    homepages_and_variations_setup: false,
    production_key_created: false,
    preview_key_created: false,
    knowledgebase_sync_job_added: false,
    reviews_sync_job_added: false,
    integrations_added: false,
    appointment_scheduling_configured: false,
    widget_configuration_done: false,
    contact_settings_adjusted: false,
    advanced_prompt_customized: false,
    ama_fully_tested: false,
    questions_fully_tested: false,
  };

  const [createTenantChecklist, setCreateTenantChecklist] = useState<CreateTenantChecklist>(defaultCreateTenantChecklist);
  const [sdlChecklist, setSdlChecklist] = useState<SdlOnboardingChecklist>(defaultChecklist);
  const [buildContentChecklist, setBuildContentChecklist] = useState<BuildClientContentChecklist>(defaultBuildContentChecklist);

  const createTenantChecklistComplete = Object.values(createTenantChecklist).every(Boolean);
  const sdlChecklistComplete = Object.values(sdlChecklist).every(Boolean);
  const buildContentChecklistComplete = Object.values(buildContentChecklist).every(Boolean);
  const queryClient = useQueryClient();

  // Initialize form with tenant data when modal opens
  useEffect(() => {
    if (tenant && isOpen) {
      setName(tenant.name);
      setStatus(tenant.status);
      setTenantType(tenant.tenant_type || 'product');
      setGoal(tenant.goal || '');
      setAggressiveness(tenant.aggressiveness || 5.0);
      setCreateTenantChecklist(tenant.create_tenant_checklist || defaultCreateTenantChecklist);
      setSdlChecklist(tenant.sdl_onboarding_checklist || defaultChecklist);
      setBuildContentChecklist(tenant.build_client_content_checklist || defaultBuildContentChecklist);
      setClientAdminEnabled(tenant.client_admin_enabled ?? true);
      setDomainExpertEnabled(tenant.domain_expert_enabled ?? false);
      setAppSubdomain(tenant.app_subdomain || defaultExpertAppSubdomain(tenant.subdomain));
      setAppSubdomainError(null);
      setPendingProductOff(null);
      setError('');
      
      // Fetch tenant logs
      setLogsLoading(true);
      apiClient.getTenantLogs(tenant.id)
        .then(setTenantLogs)
        .catch(err => {
          console.error('Failed to fetch tenant logs:', err);
          setTenantLogs([]);
        })
        .finally(() => setLogsLoading(false));
    }
  }, [tenant, isOpen]);

  const updateTenantMutation = useMutation({
    mutationFn: ({ tenantId, data }: { tenantId: string; data: TenantUpdateRequest }) => 
      apiClient.updateTenant(tenantId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      onClose();
      setError('');
      onSuccess?.();
    },
    onError: (err: Error) => {
      if ((err as ApiError).code === 'app_subdomain_taken') {
        setAppSubdomainError(APP_SUBDOMAIN_TAKEN_MESSAGE);
        return;
      }
      const message = err.message || 'Failed to update tenant';
      setError(message);
      onError?.(message);
    },
  });

  const savedClientAdminEnabled = tenant?.client_admin_enabled ?? true;
  const savedDomainExpertEnabled = tenant?.domain_expert_enabled ?? false;

  const handleClientAdminChange = (checked: boolean) => {
    if (!checked && savedClientAdminEnabled) {
      setPendingProductOff('client_admin');
      return;
    }
    setClientAdminEnabled(checked);
  };

  const handleDomainExpertChange = (checked: boolean) => {
    if (!checked && savedDomainExpertEnabled) {
      setPendingProductOff('domain_expert');
      return;
    }
    setDomainExpertEnabled(checked);
  };

  const confirmProductOff = () => {
    if (pendingProductOff === 'client_admin') setClientAdminEnabled(false);
    if (pendingProductOff === 'domain_expert') setDomainExpertEnabled(false);
    setPendingProductOff(null);
  };

  const updateCreateTenantChecklistMutation = useMutation({
    mutationFn: ({ tenantId, checklist }: { tenantId: string; checklist: CreateTenantChecklist }) =>
      apiClient.updateTenant(tenantId, { create_tenant_checklist: checklist }),
    onSuccess: (data) => {
      queryClient.setQueryData<TenantResponse[]>(['tenants'], (old) =>
        old?.map((t) => (t.id === data.id ? { ...t, create_tenant_checklist: data.create_tenant_checklist } : t))
      );
    },
  });

  const updateChecklistMutation = useMutation({
    mutationFn: ({ tenantId, checklist }: { tenantId: string; checklist: SdlOnboardingChecklist }) =>
      apiClient.updateTenant(tenantId, { sdl_onboarding_checklist: checklist }),
    onSuccess: (data) => {
      queryClient.setQueryData<TenantResponse[]>(['tenants'], (old) =>
        old?.map((t) => (t.id === data.id ? { ...t, sdl_onboarding_checklist: data.sdl_onboarding_checklist } : t))
      );
    },
  });

  const updateBuildContentChecklistMutation = useMutation({
    mutationFn: ({ tenantId, checklist }: { tenantId: string; checklist: BuildClientContentChecklist }) =>
      apiClient.updateTenant(tenantId, { build_client_content_checklist: checklist }),
    onSuccess: (data) => {
      queryClient.setQueryData<TenantResponse[]>(['tenants'], (old) =>
        old?.map((t) => (t.id === data.id ? { ...t, build_client_content_checklist: data.build_client_content_checklist } : t))
      );
    },
  });

  const handleCreateTenantChecklistToggle = (key: keyof CreateTenantChecklist, checked: boolean) => {
    if (!tenant) return;
    const updated = { ...createTenantChecklist, [key]: checked };
    setCreateTenantChecklist(updated);
    updateCreateTenantChecklistMutation.mutate({ tenantId: tenant.id, checklist: updated });
  };

  const handleChecklistToggle = (key: keyof SdlOnboardingChecklist, checked: boolean) => {
    if (!tenant) return;
    const updated = { ...sdlChecklist, [key]: checked };
    setSdlChecklist(updated);
    updateChecklistMutation.mutate({ tenantId: tenant.id, checklist: updated });
  };

  const handleBuildContentChecklistToggle = (key: keyof BuildClientContentChecklist, checked: boolean) => {
    if (!tenant) return;
    const updated = { ...buildContentChecklist, [key]: checked };
    setBuildContentChecklist(updated);
    updateBuildContentChecklistMutation.mutate({ tenantId: tenant.id, checklist: updated });
  };

  const clearCacheMutation = useMutation({
    mutationFn: (tenantId: string) => apiClient.clearTenantCache(tenantId),
    onSuccess: (data) => {
      setCacheMessage(`Successfully cleared cache! ${data.details?.total_keys_cleared || 0} cache entries removed.`);
      setCacheMessageType('success');
      setShowCacheClearModal(false);
      
      // Clear message after 5 seconds
      setTimeout(() => {
        setCacheMessage('');
        setCacheMessageType('');
      }, 5000);
    },
    onError: (error: Error) => {
      setCacheMessage(`Failed to clear cache: ${error.message}`);
      setCacheMessageType('error');
      setShowCacheClearModal(false);
      
      // Clear message after 5 seconds
      setTimeout(() => {
        setCacheMessage('');
        setCacheMessageType('');
      }, 5000);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!tenant) return;
    
    if (!name.trim()) {
      setError('Tenant name is required');
      return;
    }

    const data: TenantUpdateRequest = {
      name: name.trim(),
      status,
      tenant_type: tenantType,
      goal,
      aggressiveness
    };
    if (clientAdminEnabled !== savedClientAdminEnabled) {
      data.client_admin_enabled = clientAdminEnabled;
    }
    if (domainExpertEnabled !== savedDomainExpertEnabled) {
      data.domain_expert_enabled = domainExpertEnabled;
      if (domainExpertEnabled) {
        if (!appSubdomain) {
          setAppSubdomainError('Enter an expert app address.');
          return;
        }
        data.app_subdomain = appSubdomain;
      }
    }

    setAppSubdomainError(null);
    updateTenantMutation.mutate({ tenantId: tenant.id, data });
  };

  const handleClose = () => {
    if (!updateTenantMutation.isLoading) {
      onClose();
      setError('');
    }
  };

  const footer = (
    <>
      <button
        type="button"
        className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
        onClick={handleClose}
        disabled={updateTenantMutation.isLoading}
      >
        Cancel
      </button>
      <button
        type="submit"
        form="edit-tenant-form"
        className="inline-flex w-full justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={updateTenantMutation.isLoading}
      >
        {updateTenantMutation.isLoading ? 'Updating...' : 'Update Tenant'}
      </button>
    </>
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Edit Tenant"
        footer={footer}
        size="xl"
      >
      <form id="edit-tenant-form" onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="tenant-name" className="block text-sm font-medium text-gray-700">
            Tenant Name *
          </label>
          <input
            type="text"
            id="tenant-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:border-gray-400 sm:text-sm px-3 py-2"
            placeholder="Enter tenant name"
            disabled={updateTenantMutation.isLoading}
            required
          />
        </div>

        <div>
          <label htmlFor="tenant-subdomain" className="block text-sm font-medium text-gray-700">
            Subdomain
          </label>
          <input
            type="text"
            id="tenant-subdomain"
            value={tenant?.subdomain || ''}
            className="mt-1 block w-full rounded-md border border-gray-300 bg-gray-50 shadow-sm focus:outline-none focus:border-gray-400 sm:text-sm px-3 py-2"
            disabled
            readOnly
          />
          <p className="mt-1 text-xs text-gray-500">Subdomain cannot be changed after creation</p>
        </div>

        <div>
          <label htmlFor="tenant-status" className="block text-sm font-medium text-gray-700">
            Status *
          </label>
          <select
            id="tenant-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:border-gray-400 sm:text-sm px-3 py-2"
            disabled={updateTenantMutation.isLoading}
          >
            <option value="active">Active</option>
            <option value="building">Building</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div>
          <label htmlFor="tenant-type" className="block text-sm font-medium text-gray-700">
            Business Type *
          </label>
          <select
            id="tenant-type"
            value={tenantType}
            onChange={(e) => setTenantType(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:border-gray-400 sm:text-sm px-3 py-2"
            disabled={updateTenantMutation.isLoading}
          >
            <option value="product">Product Company</option>
            <option value="service">Service Company</option>
          </select>
          <p className="mt-1 text-xs text-gray-500">
            This determines which AI prompts and tools your customers will experience
          </p>
        </div>

        <TenantProductsField
          clientAdminEnabled={clientAdminEnabled}
          domainExpertEnabled={domainExpertEnabled}
          onClientAdminChange={handleClientAdminChange}
          onDomainExpertChange={handleDomainExpertChange}
          appSubdomain={appSubdomain}
          onAppSubdomainChange={
            savedDomainExpertEnabled
              ? undefined
              : (value) => {
                  setAppSubdomain(value);
                  setAppSubdomainError(null);
                }
          }
          appSubdomainError={appSubdomainError}
          appSubdomainHint={
            savedDomainExpertEnabled
              ? 'Change the address from the Expert Agents tab.'
              : 'Where users sign in. Defaults to the subdomain followed by -ea.'
          }
          disabled={updateTenantMutation.isLoading}
        />

        <div>
          <label htmlFor="goal" className="block text-sm font-medium text-gray-700">
            Conversation Goal
          </label>
          <textarea
            id="goal"
            rows={3}
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:border-gray-400 sm:text-sm px-3 py-2"
            placeholder="e.g., 'Help customers find the right product and complete a purchase.'"
            disabled={updateTenantMutation.isLoading}
          ></textarea>
          <p className="mt-1 text-xs text-gray-500">
            Define the primary objective for your AI assistant's conversations.
          </p>
        </div>

        {/* Aggressiveness slider temporarily hidden */}
        {/* <div>
          <label htmlFor="aggressiveness" className="block text-sm font-medium text-gray-700">
            Aggressiveness Level
          </label>
          <AggressivenessSlider
            value={aggressiveness}
            onChange={setAggressiveness}
            disabled={updateTenantMutation.isLoading}
          />
          <p className="mt-1 text-xs text-gray-500">
            Adjust how assertively the AI pursues the conversation goal.
          </p>
        </div> */}

        <div className="bg-gray-50 px-4 py-3 rounded-md">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Created:</span>
            <span className="font-medium">
              {tenant ? new Date(tenant.created_at).toLocaleDateString() : '-'}
            </span>
          </div>
          <div className="flex justify-between text-sm mt-1">
            <span className="text-gray-500">Build Date:</span>
            <span className="font-medium">
              {tenant?.build_date ? new Date(tenant.build_date).toLocaleDateString() : 'Not set'}
            </span>
          </div>
          <div className="flex justify-between text-sm mt-1">
            <span className="text-gray-500">Admin Count:</span>
            <span className="font-medium">{tenant?.admin_count || 0}</span>
          </div>
        </div>

        {/* Cache Message Display */}
        {cacheMessage && (
          <div className={`p-3 rounded-md border ${
            cacheMessageType === 'success' 
              ? 'bg-green-50 border-green-200 text-green-800' 
              : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            <div className="text-sm">{cacheMessage}</div>
          </div>
        )}

        {/* Checklists Section */}
        <div className="border-t pt-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Tenant Checklists</h4>
          <div className="space-y-2">

            {/* Create New Tenant Checklist */}
            <div className="border border-gray-200 rounded-md">
              <button
                type="button"
                onClick={() => setCreateTenantChecklistExpanded((prev) => !prev)}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-900 bg-gray-100 hover:bg-gray-200 transition-colors rounded-t-md"
              >
                <span>
                  Create New Tenant Checklist –{' '}
                  {createTenantChecklistComplete ? (
                    <span className="text-green-600 font-semibold">COMPLETE</span>
                  ) : (
                    <span className="text-yellow-600 font-semibold">IN-PROCESS</span>
                  )}
                </span>
                {createTenantChecklistExpanded ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </button>
              {createTenantChecklistExpanded && (
                <div className="border-t border-gray-200 px-4 py-3 space-y-2">
                  <div className="flex items-start gap-2 text-sm text-gray-700">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={createTenantChecklist.tenant_created_on_lawmabase}
                        onChange={(e) => handleCreateTenantChecklistToggle('tenant_created_on_lawmabase', e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>Create the Tenant Record in Lawmabase</span>
                    </label>
                    <div className="relative ml-1">
                      <HelpCircle className="h-4 w-4 text-gray-400 hover:text-primary-600 cursor-pointer mt-0.5" onClick={() => setOpenTooltip(openTooltip === 'tenant_created' ? null : 'tenant_created')} />
                      {openTooltip === 'tenant_created' && (
                        <div className="absolute left-6 top-0 z-50 w-96 max-h-[30rem] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-700">
                          <div>
                            <p className="font-medium mb-1">Please follow these instructions</p>
                            <hr className="my-1 border-gray-200" />
                            <p><strong>Step 1:</strong> Log in to lawmabase admin panel</p>
                            <p><strong>Step 2:</strong> Navigate to Tenants and click "Create New"</p>
                            <p><strong>Step 3:</strong> Fill in the tenant name and subdomain (convention is to use all lowercase no dashes. e.g. claim exchange would be claimexchange</p>
                            <p><strong>Step 4:</strong> Configure tenant type and settings (default is Service company)</p>
                            <p><strong>Step 5:</strong> Click "Create" and verify the tenant appears in the list</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-700">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={createTenantChecklist.dns_entries_added}
                        onChange={(e) => handleCreateTenantChecklistToggle('dns_entries_added', e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>Configure DNS Records with the lawmabase.com Domain Provider</span>
                    </label>
                    <div className="relative ml-1">
                      <HelpCircle className="h-4 w-4 text-gray-400 hover:text-primary-600 cursor-pointer mt-0.5" onClick={() => setOpenTooltip(openTooltip === 'dns_entries' ? null : 'dns_entries')} />
                      {openTooltip === 'dns_entries' && (
                        <div className="absolute left-6 top-0 z-50 w-96 max-h-[30rem] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-700">
                          <div>
                            <p className="font-medium mb-1">Please follow these instructions</p>
                            <hr className="my-1 border-gray-200" />
                            <p><strong>Step 1:</strong> Open up Namecheap under the tylerburton login (this will be changed soon)</p>
                            <p><strong>Step 2:</strong> Go to the domain list</p>
                            <p><strong>Step 3:</strong> Click Manage</p>
                            <p><strong>Step 4:</strong> Go to the Advanced DNS tab</p>
                            <p><strong>Step 5:</strong> Click Add New Record &rarr; CNAME</p>
                            <p><strong>Step 6:</strong> For value, use the current default lawmabase: <span className="text-xs break-all font-mono">app-lawma-prod-cust-admin-westus2-001-gmdhgvb2h7exd5h8.westus2-01.azurewebsites.net</span></p>
                            <p><strong>Step 7:</strong> Host should just be the first part of the domain name</p>
                            <p><strong>Step 8:</strong> Change TTL to 1 min</p>
                            <p><strong>Step 9:</strong> Save it</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-700">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={createTenantChecklist.custom_domains_added_in_azure}
                        onChange={(e) => handleCreateTenantChecklistToggle('custom_domains_added_in_azure', e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>Add custom domains in Azure web app for client admin</span>
                    </label>
                    <div className="relative ml-1">
                      <HelpCircle className="h-4 w-4 text-gray-400 hover:text-primary-600 cursor-pointer mt-0.5" onClick={() => setOpenTooltip(openTooltip === 'custom_domains' ? null : 'custom_domains')} />
                      {openTooltip === 'custom_domains' && (
                        <div className="absolute left-6 top-0 z-50 w-96 max-h-[30rem] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-700">
                          <div>
                            <p className="font-medium mb-1">Please follow these instructions</p>
                            <hr className="my-1 border-gray-200" />
                            <p><strong>Step 1:</strong> Login to portal.azure.com</p>
                            <p><strong>Step 2:</strong> Go to the web app: app-lawma-prod-cust-admin-westus2-001</p>
                            <p><strong>Step 3:</strong> Go to Settings &rarr; Custom domains</p>
                            <p><strong>Step 4:</strong> Click Add custom domain</p>
                            <p><strong>Step 5:</strong> Under Add custom domain, select "All other domain services"</p>
                            <p><strong>Step 6:</strong> Select "Add certificate later"</p>
                            <p><strong>Step 7:</strong> Enter the domain, e.g. initus.lawmabase.com</p>
                            <p><strong>Step 8:</strong> Click Validate</p>
                            <p><strong>Step 9:</strong> Click Add</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-700">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={createTenantChecklist.ssl_certificates_created_and_tested}
                        onChange={(e) => handleCreateTenantChecklistToggle('ssl_certificates_created_and_tested', e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>Create and test ssl certificates</span>
                    </label>
                    <div className="relative ml-1">
                      <HelpCircle className="h-4 w-4 text-gray-400 hover:text-primary-600 cursor-pointer mt-0.5" onClick={() => setOpenTooltip(openTooltip === 'ssl_certs' ? null : 'ssl_certs')} />
                      {openTooltip === 'ssl_certs' && (
                        <div className="absolute left-6 top-0 z-50 w-96 max-h-[30rem] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-700">
                          <div>
                            <p className="font-medium mb-1">Please follow these instructions</p>
                            <hr className="my-1 border-gray-200" />
                            <p><strong>Step 1:</strong> Find the custom domain you just added in the list of custom domains.</p>
                            <p><strong>Step 2:</strong> Click the "Add binding" link</p>
                            <p><strong>Step 3:</strong> Make sure "Add new certificate" is selected</p>
                            <p><strong>Step 4:</strong> Under Source, make sure it says "Create App Service Managed Certificate"</p>
                            <p><strong>Step 5:</strong> Click Validate</p>
                            <p><strong>Step 6:</strong> Click Add</p>
                            <p><strong>Step 7:</strong> After the cert is ready you will see a notification</p>
                            <p><strong>Step 8:</strong> You will also see the Status in the list changed to "Secured"</p>
                            <p><strong>Step 9:</strong> Go to the domain and make sure it resolves and has no SSL errors</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* SDL Onboarding Checklist */}
            <div className="border border-gray-200 rounded-md">
              <button
                type="button"
                onClick={() => setSdlChecklistExpanded((prev) => !prev)}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-900 bg-gray-100 hover:bg-gray-200 transition-colors rounded-t-md"
              >
                <span>
                  SDL Onboarding Checklist –{' '}
                  {sdlChecklistComplete ? (
                    <span className="text-green-600 font-semibold">COMPLETE : READY FOR LARS</span>
                  ) : (
                    <span className="text-yellow-600 font-semibold">IN-PROCESS</span>
                  )}
                </span>
                {sdlChecklistExpanded ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </button>
              {sdlChecklistExpanded && (
                <div className="border-t border-gray-200 px-4 py-3 space-y-2">
                  <div className="flex items-start gap-2 text-sm text-gray-700">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sdlChecklist.crawl_mds_uploaded}
                        onChange={(e) => handleChecklistToggle('crawl_mds_uploaded', e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>Upload crawl .mds to TenantData/{tenant?.subdomain || '{subdomain}'}/crawl/md/documents</span>
                    </label>
                    <div className="relative ml-1">
                      <HelpCircle className="h-4 w-4 text-gray-400 hover:text-primary-600 cursor-pointer mt-0.5" onClick={() => setOpenTooltip(openTooltip === 'crawl_mds' ? null : 'crawl_mds')} />
                      {openTooltip === 'crawl_mds' && (
                        <div className="absolute left-6 top-0 z-50 w-96 max-h-[30rem] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-700">
                          <div>
                            <p className="font-medium mb-1">Please follow these instructions</p>
                            <hr className="my-1 border-gray-200" />
                            <p><strong>Step 1:</strong> Go to Apify and perform a scrape for the company with a start url equal to the url of the company</p>
                            <p><strong>Step 2:</strong> Validate the scrape completed and has a good response for the main pages. It will be necessary to look at md output and do a quick scan of the core pages to ensure that content is not being skipped. (Sometimes the scraper will include routes that are not needed, in this case stop the scrape job and add exclusions and re-run)</p>
                            <p><strong>Step 3:</strong> (not always needed) Sometimes the default Apify Scraper (Website Content Crawler) might miss content on a page. As an alternative the Cloudflare md scraper option can be used for a single route and often remedies the problem.</p>
                            <p><strong>Step 4:</strong> Download the scrape: Do this by going to Runs and locate your run by clicking on it and inspecting the output. Once you have identified your run, change your result from Text to Markdown and click JSON. Copy the contents into a new file or alternatively into a Claude window.</p>
                            <p><strong>Step 5:</strong> From the json results put the following prompt in Claude: "Please take this .json file and produce individual md files for me. Follow the format name:&#123;url&#125; followed by a newline content:&#123;text&#125;"</p>
                            <p><strong>Step 6:</strong> Take these files and upload them to the Source of Truth under TenantData/&#123;yourcompany&#125;/crawl/md/documents</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-700">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sdlChecklist.marketing_audit_uploaded}
                        onChange={(e) => handleChecklistToggle('marketing_audit_uploaded', e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>Upload MARKETING-AUDIT.md to TenantData/{tenant?.subdomain || '{subdomain}'}/doc-prep-raw/audit-report/MARKETING-AUDIT.md</span>
                    </label>
                    <div className="relative ml-1">
                      <HelpCircle className="h-4 w-4 text-gray-400 hover:text-primary-600 cursor-pointer mt-0.5" onClick={() => setOpenTooltip(openTooltip === 'marketing_audit' ? null : 'marketing_audit')} />
                      {openTooltip === 'marketing_audit' && (
                        <div className="absolute left-6 top-0 z-50 w-96 max-h-[30rem] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-700">
                          <div>
                            <p className="font-medium mb-1">Please follow these instructions</p>
                            <hr className="my-1 border-gray-200" />
                            <p><strong>Step 1:</strong> From the marketing audit project (ask if you don't know) run /market-audit &#123;company_url&#125;</p>
                            <p><strong>Step 2:</strong> Wait for it to finish</p>
                            <p><strong>Step 3:</strong> Copy the resulting marketing audit md file to TenantData/&#123;company&#125;/doc-prep-raw/audit-report/MARKETING-AUDIT.md</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-700">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sdlChecklist.competitor_crawl_uploaded}
                        onChange={(e) => handleChecklistToggle('competitor_crawl_uploaded', e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>Upload competitor crawl .json(s) to TenantData/{tenant?.subdomain || '{subdomain}'}/doc-prep-raw/competitors-crawl</span>
                    </label>
                    <div className="relative ml-1">
                      <HelpCircle className="h-4 w-4 text-gray-400 hover:text-primary-600 cursor-pointer mt-0.5" onClick={() => setOpenTooltip(openTooltip === 'competitor_crawl' ? null : 'competitor_crawl')} />
                      {openTooltip === 'competitor_crawl' && (
                        <div className="absolute left-6 top-0 z-50 w-96 max-h-[30rem] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-700">
                          <div>
                            <p className="font-medium mb-1">Please follow these instructions</p>
                            <hr className="my-1 border-gray-200" />
                            <p><strong>Step 1:</strong> There will be a file at TenantData/&#123;subdomain&#125;/doc-prep-raw/competitors-crawl with a list of competitor web sites</p>
                            <p><strong>Step 2:</strong> Use Apify to crawl each competitor site</p>
                            <p><strong>Step 3:</strong> Upload the resulting .json files to TenantData/&#123;company&#125;/doc-prep-raw/competitors-crawl</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-700">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sdlChecklist.questionaire_uploaded}
                        onChange={(e) => handleChecklistToggle('questionaire_uploaded', e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>Upload {tenant?.subdomain || '{subdomain}'}_raw_questionaire_transcript.txt to TenantData/{tenant?.subdomain || '{subdomain}'}/doc-prep-raw/questionaire/{tenant?.subdomain || '{subdomain}'}_raw_questionaire_transcript.txt</span>
                    </label>
                    <div className="relative ml-1">
                      <HelpCircle className="h-4 w-4 text-gray-400 hover:text-primary-600 cursor-pointer mt-0.5" onClick={() => setOpenTooltip(openTooltip === 'questionaire' ? null : 'questionaire')} />
                      {openTooltip === 'questionaire' && (
                        <div className="absolute left-6 top-0 z-50 w-96 max-h-[30rem] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-700">
                          <div>
                            <p className="font-medium mb-1">Please follow these instructions</p>
                            <hr className="my-1 border-gray-200" />
                            <p><strong>Step 1:</strong> Take the file from the Zoom meeting for the questionaire meeting and put it in &#123;companyname&#125;_raw_questionaire_transcript.txt to TenantData/&#123;companyname&#125;/doc-prep-raw/questionaire/&#123;companyname&#125;_raw_questionaire_transcript.txt (tip: if the file from Zoom is .vtt just change it to .txt)</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-700">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sdlChecklist.other_mds_uploaded}
                        onChange={(e) => handleChecklistToggle('other_mds_uploaded', e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>Upload other .mds to TenantData/{tenant?.subdomain || '{subdomain}'}/other/md/documents</span>
                    </label>
                    <div className="relative ml-1">
                      <HelpCircle className="h-4 w-4 text-gray-400 hover:text-primary-600 cursor-pointer mt-0.5" onClick={() => setOpenTooltip(openTooltip === 'other_mds' ? null : 'other_mds')} />
                      {openTooltip === 'other_mds' && (
                        <div className="absolute left-6 top-0 z-50 w-96 max-h-[30rem] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-700">
                          <div>
                            <p className="font-medium mb-1">Please follow these instructions</p>
                            <hr className="my-1 border-gray-200" />
                            <p><strong>Step 1:</strong> For each extra file whether a pdf, doc or otherwise use a prompt like: "Please take this .pdf file and produce individual md files for me. Follow the format name:&#123;name of the file&#125; followed by a newline content:&#123;contents of the file&#125;"</p>
                            <p><strong>Step 2:</strong> Upload all resulting md files to the Source of Truth under TenantData/&#123;yourcompany&#125;/other/md/documents</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-700">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sdlChecklist.sdl_generated}
                        onChange={(e) => handleChecklistToggle('sdl_generated', e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>Generate SDL</span>
                    </label>
                    <div className="relative ml-1">
                      <HelpCircle className="h-4 w-4 text-gray-400 hover:text-primary-600 cursor-pointer mt-0.5" onClick={() => setOpenTooltip(openTooltip === 'sdl_generated' ? null : 'sdl_generated')} />
                      {openTooltip === 'sdl_generated' && (
                        <div className="absolute left-6 top-0 z-50 w-96 max-h-[30rem] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-700">
                          <div>
                            <p className="font-medium mb-1">Please follow these instructions</p>
                            <hr className="my-1 border-gray-200" />
                            <p><strong>Step 1:</strong> Checkout repo sdl</p>
                            <p><strong>Step 2:</strong> Open the project in Visual Studio Code</p>
                            <p><strong>Step 3:</strong> Run a Claude Code terminal</p>
                            <p><strong>Step 4:</strong> Ask Claude to create a run for your company</p>
                            <p><strong>Step 5:</strong> Copy all input files from SOT into the run input folder</p>
                            <p><strong>Step 6:</strong> Ask Claude to run all steps for &#123;run_name&#125;</p>
                            <p><strong>Step 7:</strong> Validate outputs</p>
                            <p><strong>Step 8:</strong> Tweak and re-run as necessary</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-700">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sdlChecklist.sdl_uploaded_to_qm}
                        onChange={(e) => handleChecklistToggle('sdl_uploaded_to_qm', e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>Uploaded SDL to Question Management for {tenant?.subdomain || '{subdomain}'}</span>
                    </label>
                    <div className="relative ml-1">
                      <HelpCircle className="h-4 w-4 text-gray-400 hover:text-primary-600 cursor-pointer mt-0.5" onClick={() => setOpenTooltip(openTooltip === 'sdl_uploaded_qm' ? null : 'sdl_uploaded_qm')} />
                      {openTooltip === 'sdl_uploaded_qm' && (
                        <div className="absolute left-6 top-0 z-50 w-96 max-h-[30rem] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-700">
                          <div>
                            <p className="font-medium mb-1">Please follow these instructions</p>
                            <hr className="my-1 border-gray-200" />
                            <p><strong>Step 1:</strong> Take the upsert script from the result of the SDL GEN</p>
                            <p><strong>Step 2:</strong> Open tenant_auth db and get the tenant GUID for your company</p>
                            <p><strong>Step 3:</strong> Update the upsert script with the tenant GUID everywhere where the script is using a blank guid 00000-00000-000...</p>
                            <p><strong>Step 4:</strong> Test the script in the transaction with the rollback</p>
                            <p><strong>Step 5:</strong> Change to commit and run</p>
                            <p><strong>Step 6:</strong> Validate your SDL load in Questions Management</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-700">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sdlChecklist.complete_sdl_qa_pass}
                        onChange={(e) => handleChecklistToggle('complete_sdl_qa_pass', e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>Complete SDL QA Pass</span>
                    </label>
                    <div className="relative ml-1">
                      <HelpCircle className="h-4 w-4 text-gray-400 hover:text-primary-600 cursor-pointer mt-0.5" onClick={() => setOpenTooltip(openTooltip === 'sdl_qa_pass' ? null : 'sdl_qa_pass')} />
                      {openTooltip === 'sdl_qa_pass' && (
                        <div className="absolute left-6 top-0 z-50 w-96 max-h-[30rem] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-700">
                          <div>
                            <p className="font-medium mb-1">Please follow these instructions</p>
                            <hr className="my-1 border-gray-200" />
                            <p>Now that the SDL has been generated, Quality check the entire SDL for completeness and correctness. Make any changes necessary to the questions directly in Questions Management.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Build Client Content Checklist */}
            <div className="border border-gray-200 rounded-md">
              <button
                type="button"
                onClick={() => setBuildContentChecklistExpanded((prev) => !prev)}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-900 bg-gray-100 hover:bg-gray-200 transition-colors rounded-t-md"
              >
                <span>
                  Build Client Content Checklist –{' '}
                  {buildContentChecklistComplete ? (
                    <span className="text-green-600 font-semibold">COMPLETE</span>
                  ) : (
                    <span className="text-yellow-600 font-semibold">IN-PROCESS</span>
                  )}
                </span>
                {buildContentChecklistExpanded ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </button>
              {buildContentChecklistExpanded && (
                <div className="border-t border-gray-200 px-4 py-3 space-y-2">
                  <div className="flex items-start gap-2 text-sm text-gray-700">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={buildContentChecklist.assets_added}
                        onChange={(e) => handleBuildContentChecklistToggle('assets_added', e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>Add Assets via Client Admin</span>
                    </label>
                    <div className="relative ml-1">
                      <HelpCircle className="h-4 w-4 text-gray-400 hover:text-primary-600 cursor-pointer mt-0.5" onClick={() => setOpenTooltip(openTooltip === 'assets_added' ? null : 'assets_added')} />
                      {openTooltip === 'assets_added' && (
                        <div className="absolute left-6 top-0 z-50 w-96 max-h-[30rem] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-700">
                          <div>
                            <p className="font-medium mb-1">Please follow these instructions</p>
                            <hr className="my-1 border-gray-200" />
                            <p><strong>Step 1:</strong> Go to Apify and run the Image Downloader Actor for the site</p>
                            <p><strong>Step 2:</strong> When complete you can inspect the run and download the zip file</p>
                            <p><strong>Step 3:</strong> Unzip the zip file and remove any images that you don't want to upload</p>
                            <p><strong>Step 4:</strong> Open the Client Admin for the company and go to Content Management &rarr; Assets</p>
                            <p><strong>Step 5:</strong> Click Upload Assets</p>
                            <p><strong>Step 6:</strong> Drag in 20 or 30 (can't remember the max ATM) items at a time and upload</p>
                            <p><strong>Step 7:</strong> Rinse and repeat</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={buildContentChecklist.faq_category_labels_created}
                      onChange={(e) => handleBuildContentChecklistToggle('faq_category_labels_created', e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>Create necessary labels for FAQ categories for Questions and test</span>
                  </label>
                  <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={buildContentChecklist.ctas_setup}
                      onChange={(e) => handleBuildContentChecklistToggle('ctas_setup', e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>Setup CTAs</span>
                  </label>
                  <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={buildContentChecklist.homepages_and_variations_setup}
                      onChange={(e) => handleBuildContentChecklistToggle('homepages_and_variations_setup', e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>Setup HomePages and HomePage Variations</span>
                  </label>
                  <div className="flex items-start gap-2 text-sm text-gray-700">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={buildContentChecklist.production_key_created}
                        onChange={(e) => handleBuildContentChecklistToggle('production_key_created', e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>Create Production Key</span>
                    </label>
                    <div className="relative ml-1">
                      <HelpCircle className="h-4 w-4 text-gray-400 hover:text-primary-600 cursor-pointer mt-0.5" onClick={() => setOpenTooltip(openTooltip === 'production_key' ? null : 'production_key')} />
                      {openTooltip === 'production_key' && (
                        <div className="absolute left-6 top-0 z-50 w-96 max-h-[30rem] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-700">
                          <div>
                            <p className="font-medium mb-1">Please follow these instructions</p>
                            <hr className="my-1 border-gray-200" />
                            <p><strong>Step 1:</strong> Go to Client Admin for the company</p>
                            <p><strong>Step 2:</strong> Go to API Keys</p>
                            <p><strong>Step 3:</strong> Click Create API Key</p>
                            <p><strong>Step 4:</strong> For the name use &#123;Company Name&#125; Production Key</p>
                            <p><strong>Step 5:</strong> Copy the key into the company's onboarding email or doc for distribution to the company during setup</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-700">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={buildContentChecklist.preview_key_created}
                        onChange={(e) => handleBuildContentChecklistToggle('preview_key_created', e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>Create Preview Key</span>
                    </label>
                    <div className="relative ml-1">
                      <HelpCircle className="h-4 w-4 text-gray-400 hover:text-primary-600 cursor-pointer mt-0.5" onClick={() => setOpenTooltip(openTooltip === 'preview_key' ? null : 'preview_key')} />
                      {openTooltip === 'preview_key' && (
                        <div className="absolute left-6 top-0 z-50 w-96 max-h-[30rem] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-700">
                          <div>
                            <p className="font-medium mb-1">Please follow these instructions</p>
                            <hr className="my-1 border-gray-200" />
                            <p><strong>Step 1:</strong> Go to Client Admin for the company</p>
                            <p><strong>Step 2:</strong> Go to API Keys</p>
                            <p><strong>Step 3:</strong> Click Create API Key</p>
                            <p><strong>Step 4:</strong> For the name use &#123;Company Name&#125; Preview Key</p>
                            <p><strong>Step 5:</strong> Make sure to check the preview checkbox</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-700">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={buildContentChecklist.knowledgebase_sync_job_added}
                        onChange={(e) => handleBuildContentChecklistToggle('knowledgebase_sync_job_added', e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>Add a Knowledgebase sync job in lawmabase and do first sync; quickly validate resulting knowledge base</span>
                    </label>
                    <div className="relative ml-1">
                      <HelpCircle className="h-4 w-4 text-gray-400 hover:text-primary-600 cursor-pointer mt-0.5" onClick={() => setOpenTooltip(openTooltip === 'knowledgebase_sync' ? null : 'knowledgebase_sync')} />
                      {openTooltip === 'knowledgebase_sync' && (
                        <div className="absolute left-6 top-0 z-50 w-96 max-h-[30rem] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-700">
                          <div>
                            <p className="font-medium mb-1">Please follow these instructions</p>
                            <hr className="my-1 border-gray-200" />
                            <p><strong>Step 1:</strong> Go to lawmabase as Google User lawma@lawmabase.com</p>
                            <p><strong>Step 2:</strong> Make sure there is not already a Pull Knowledge Base task for this company</p>
                            <p><strong>Step 3:</strong> Go to Tasks in the left menu</p>
                            <p><strong>Step 4:</strong> Click New Task</p>
                            <p><strong>Step 5:</strong> Select Task "Pull Knowledge Base"</p>
                            <p><strong>Step 6:</strong> Select monthly frequency</p>
                            <p><strong>Step 7:</strong> Select your company's tenant record</p>
                            <p><strong>Step 8:</strong> Connect to Google Drive by clicking the button</p>
                            <p><strong>Step 9:</strong> Create the Task</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-700">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={buildContentChecklist.reviews_sync_job_added}
                        onChange={(e) => handleBuildContentChecklistToggle('reviews_sync_job_added', e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>Add reviews sync job in lawmabase and do first run; validate reviews exists in Client Admin</span>
                    </label>
                    <div className="relative ml-1">
                      <HelpCircle className="h-4 w-4 text-gray-400 hover:text-primary-600 cursor-pointer mt-0.5" onClick={() => setOpenTooltip(openTooltip === 'reviews_sync' ? null : 'reviews_sync')} />
                      {openTooltip === 'reviews_sync' && (
                        <div className="absolute left-6 top-0 z-50 w-96 max-h-[30rem] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-700">
                          <div>
                            <p className="font-medium mb-1">Please follow these instructions</p>
                            <hr className="my-1 border-gray-200" />
                            <p><strong>Step 1:</strong> Make sure there is not already a Pull Reviews task for this company</p>
                            <p><strong>Step 2:</strong> Go to Tasks in the left menu</p>
                            <p><strong>Step 3:</strong> Click New Task</p>
                            <p><strong>Step 4:</strong> Select Task "Pull Reviews"</p>
                            <p><strong>Step 5:</strong> Select monthly frequency</p>
                            <p><strong>Step 6:</strong> Select your company's tenant record</p>
                            <p><strong>Step 7:</strong> Generate Place ID by using the Generate Place ID button and selecting the correct Google Places Record</p>
                            <p><strong>Step 8:</strong> Create the Task</p>
                            <p><strong>Step 9:</strong> Test it! Google will only pull 5 reviews at a time.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={buildContentChecklist.integrations_added}
                      onChange={(e) => handleBuildContentChecklistToggle('integrations_added', e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>Add necessary integrations</span>
                  </label>
                  <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={buildContentChecklist.appointment_scheduling_configured}
                      onChange={(e) => handleBuildContentChecklistToggle('appointment_scheduling_configured', e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>Turn on Appointment scheduling if the client wants it</span>
                  </label>
                  <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={buildContentChecklist.widget_configuration_done}
                      onChange={(e) => handleBuildContentChecklistToggle('widget_configuration_done', e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>Do any additional Widget Configuration necessary to properly support the clients brand</span>
                  </label>
                  <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={buildContentChecklist.contact_settings_adjusted}
                      onChange={(e) => handleBuildContentChecklistToggle('contact_settings_adjusted', e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>Adjust contact settings in Tenant Settings Contacts to reflect what the company has supplied</span>
                  </label>
                  <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={buildContentChecklist.advanced_prompt_customized}
                      onChange={(e) => handleBuildContentChecklistToggle('advanced_prompt_customized', e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>Customize Advanced prompt IF customer has special behaviors that are not captured by LAWMA defaults</span>
                  </label>
                  <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={buildContentChecklist.ama_fully_tested}
                      onChange={(e) => handleBuildContentChecklistToggle('ama_fully_tested', e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>Fully test AMA</span>
                  </label>
                  <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={buildContentChecklist.questions_fully_tested}
                      onChange={(e) => handleBuildContentChecklistToggle('questions_fully_tested', e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>Full test Questions and make any final adjustments before signing off on initial Questions</span>
                  </label>
                </div>
              )}
            </div>

          </div>
        </div>

        {/* Tenant Actions Section */}
        <div className="border-t pt-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Tenant Actions</h4>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setShowCacheClearModal(true)}
              className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              disabled={clearCacheMutation.isLoading}
            >
              {clearCacheMutation.isLoading ? 'Clearing...' : 'Clear Cache'}
            </button>
          </div>
        </div>

        {/* Tenant Logs Section */}
        <div className="border-t pt-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Tenant Logs</h4>
          {logsLoading ? (
            <div className="text-center py-4">
              <p className="text-gray-500 text-sm">Loading logs...</p>
            </div>
          ) : tenantLogs.length > 0 ? (
            <div className="max-h-60 overflow-y-auto space-y-2">
              {tenantLogs.map((log, index) => (
                <div key={log.id || index} className="bg-white border border-gray-200 rounded-md p-3 text-sm">
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <span className="font-medium text-gray-700 block mb-1">Date:</span>
                      <p className="text-gray-600">{new Date(log.created_at).toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700 block mb-1">Message:</span>
                      <p className="text-gray-600 break-words">{log.message}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700 block mb-1">Status:</span>
                      <p className="text-gray-600">{log.current_status}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm text-center py-4">No logs available</p>
          )}
        </div>
      </form>
    </Modal>

    <ConfirmationModal
      isOpen={pendingProductOff !== null}
      onClose={() => setPendingProductOff(null)}
      onConfirm={confirmProductOff}
      title={pendingProductOff === 'domain_expert' ? 'Turn off Domain Expert Agents?' : 'Turn off Client Admin?'}
      message={
        pendingProductOff === 'domain_expert'
          ? 'Signs out all users immediately. Data is kept.'
          : "Hides Client Admin from this tenant's admins. The website widget keeps working; disable its API keys if it should stop."
      }
      confirmText="Turn off"
      type="warning"
    />

    {/* Cache Clear Confirmation Modal */}
    {showCacheClearModal && (
      <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowCacheClearModal(false)}></div>
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                    Clear Cache?
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      This will clear all cached data for this tenant. This is a safe operation that will refresh the AI agent with the latest data. The tenant may experience slightly longer load times on their next requests.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-orange-600 text-base font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                onClick={() => tenant && clearCacheMutation.mutate(tenant.id)}
                disabled={clearCacheMutation.isLoading}
              >
                {clearCacheMutation.isLoading ? 'Clearing...' : 'Clear Cache'}
              </button>
              <button
                type="button"
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={() => setShowCacheClearModal(false)}
                disabled={clearCacheMutation.isLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default EditTenantModal;
